/* Import center modes. Only `live` modes can be opened; the rest render as "Upcoming". */

import { CircleDot, LayoutGrid, ArrowLeftRight, type LucideIcon } from 'lucide-react'
import type { Persona } from '@/lib/session/session.types'

export type ImportMode = 'single-client' | 'client-portfolios' | 'multi-client'

export interface ImportModeOption {
  key: ImportMode
  live: boolean
  icon: LucideIcon
  title: string
  description: string
  /** Short left-to-right flow shown on the card. */
  flow: string[]
  /** Two footer facts. */
  facts: [string, string]
  /** Relative control burden, 0–100 — drawn as the card's complexity bar. */
  complexity: number
  /** `advanced` modes carry a warning tint: they need more control from the importer. */
  advanced?: boolean
  /**
   * Personas that may see and open this mode. Absent = every persona. A mode a persona may not use is
   * not rendered on the landing, and a URL naming it falls back to the landing (PRD FR-1). The server
   * refuses independently — this is presentation, not the security boundary.
   */
  personas?: Persona[]
}

export const IMPORT_MODES: ImportModeOption[] = [
  {
    key: 'single-client',
    live: true,
    icon: CircleDot,
    title: 'Entities for one client',
    description: 'Normal day-to-day onboarding into one locked client portfolio.',
    flow: ['Client', 'Upload', 'Import'],
    facts: ['Lowest complexity', 'Client-scoped'],
    complexity: 28,
  },
  {
    key: 'client-portfolios',
    live: true,
    personas: ['SA', 'AGENCY'],
    icon: LayoutGrid,
    title: 'Bulk import client portfolios',
    description: 'Create or match many client portfolios before entity import.',
    flow: ['Clients', 'Match', 'Portfolios'],
    facts: ['Book-of-business migration', 'Portfolio setup'],
    complexity: 48,
  },
  {
    key: 'multi-client',
    live: false,
    icon: ArrowLeftRight,
    title: 'Entities across multiple clients',
    description: 'Use when every row has a reliable client identifier.',
    flow: ['Client ID', 'Assign', 'Match'],
    facts: ['Higher control requirement', 'Assignment first'],
    complexity: 82,
    advanced: true,
  },
]

/** Whether `persona` may open `mode`. A mode without a persona list is open to every persona. */
export function modeAllowedFor(mode: ImportMode, persona: Persona | undefined): boolean {
  const option = IMPORT_MODES.find((m) => m.key === mode)
  if (!option?.personas) return true
  return persona !== undefined && option.personas.includes(persona)
}
